import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Box, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Alert, List, ListItem, ListItemText, Chip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwitchPubLab from "./SwitchPubLab";
import StatusCell from './StatusCell';
import LabDetailPanel from './LabDetailPanel';
import { useLabData } from './useLabData';

// La definizione delle colonne è ora più pulita e riutilizzabile - OTTIMIZZATA PER EVITARE SCROLL ORIZZONTALE
const getColumns = () => [
    { accessorKey: 'email', header: 'E-Mail', size: 120 },
    { accessorKey: 'idLab', header: 'Id Lab', size: 90 },
    { accessorKey: 'labName', header: 'Nome Lab', size: 120 },
    {
        accessorKey: "labType", header: "Tipo", size: 80,
        Cell: ({ cell }) => (
            <>
                {cell.row.original.labType}
                {cell.row.original.labVersion === 2 && (
                    <span
                        style={{
                            marginLeft: 4, background: "#1976d2", color: "#fff", borderRadius: "4px",
                            padding: "1px 4px", fontSize: "0.65em", fontWeight: "bold",
                            verticalAlign: "middle", display: "inline-block"
                        }}
                        title="Lab di nuova generazione"
                    >
                        V2
                    </span>
                )}
            </>
        ),
    },
    {
        accessorKey: "registerUrlStatus", header: "Iscr.", size: 70, enableSorting: false,
        Cell: ({ cell }) => <SwitchPubLab idLab={cell.row.original.idLab} registerUrlStatus={cell.row.original.registerUrlStatus} />,
    },
    {
        accessorKey: "statusChart", header: "Avanzamento", size: 120, enableSorting: false,
        Cell: ({ cell }) => <StatusCell {...cell.row.original.statusChartProps} />,
    },
    {
        accessorKey: "operations", header: "Attività di sistema", size: 150, enableSorting: false,
        Cell: ({ cell }) => {
            const { onOpenOperationsModal } = cell.row.original;
            return (
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onOpenOperationsModal) {
                            onOpenOperationsModal(cell.row.original.idLab);
                        }
                    }}
                    title="Visualizza attività di sistema"
                    sx={{ color: '#a71e3b', '&:hover': { backgroundColor: 'rgba(167,30,59,0.1)' } }}
                >
                    <SearchIcon fontSize="small" />
                </IconButton>
            );
        },
    },
    { accessorKey: "nextActivity", header: "Prossima attività", size: 130 },
    {
        accessorKey: "physicalLabs",
        header: "Lab fisici",
        size: 90,
        Cell: ({ cell }) => {
            return Array.isArray(cell.row.original.physicalLabs) ? cell.row.original.physicalLabs.join(", ") : "";
        },
        enableSorting: false,
    },
];


// Helper function to safely parse JSON responses
const safeJsonFetch = async (url) => {
    const response = await fetch(url, {
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Errore HTTP ${response.status}`);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Nessuna operazione trovata');
    }

    try {
        const data = await response.json();
        return data;
    } catch (err) {
        throw new Error('Nessuna operazione trovata');
    }
};

const OperationHistoryModal = ({ open, onClose, idLab }) => {
    const [operations, setOperations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeOperation, setActiveOperation] = useState(null);

    useEffect(() => {
        if (!open || !idLab) return;

        const fetchOperations = async () => {
            try {
                const url = process.env.REACT_APP_URL_OPERATIONS_LAB + `/${idLab}`;
                setLoading(true);
                setError(null);
                const data = await safeJsonFetch(url);
                setOperations(Array.isArray(data) ? data : []);
            } catch (err) {
                setError(err.message);
                console.error('Errore:', err);
                setOperations([]);
            } finally {
                setLoading(false);
            }
        };

        fetchOperations();
    }, [open, idLab]);

    const handleOperationClick = (operation) => {
        setActiveOperation(operation);
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed': return 'success';
            case 'failed': return 'error';
            case 'pending': return 'warning';
            case 'in_progress': return 'info';
            default: return 'default';
        }
    };

    const handleCloseModal = () => {
        setActiveOperation(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleCloseModal} maxWidth="md" fullWidth>
            <DialogTitle>Storico Operazioni - Lab {idLab}</DialogTitle>
            <DialogContent sx={{ maxHeight: '500px', overflow: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {!activeOperation ? (
                    <>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : operations.length > 0 ? (
                            <List>
                                {operations.map((op) => (
                                    <ListItem
                                        key={op.operationId}
                                        onClick={() => handleOperationClick(op)}
                                        sx={{
                                            cursor: 'pointer',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px',
                                            mb: 1,
                                            '&:hover': { backgroundColor: '#f5f5f5' }
                                        }}
                                    >
                                        <ListItemText
                                            primary={`${op.orchestrator} - ${op.operationId}`}
                                            secondary={
                                                <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                                                    <Chip
                                                        label={op.finalStatus}
                                                        size="small"
                                                        color={getStatusColor(op.finalStatus)}
                                                        variant="outlined"
                                                    />
                                                    <Typography variant="caption" color="textSecondary">
                                                        {new Date(op.startedAt).toLocaleString('it-IT')}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography sx={{ p: 2 }}>Nessuna operazione trovata</Typography>
                        )}
                    </>
                ) : (
                    <Box>
                        <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {activeOperation.orchestrator} - {activeOperation.operationId}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                <Chip
                                    label={activeOperation.finalStatus}
                                    color={getStatusColor(activeOperation.finalStatus)}
                                    variant="outlined"
                                />
                                <Typography variant="body2" color="textSecondary">
                                    {new Date(activeOperation.startedAt).toLocaleString('it-IT')}
                                </Typography>
                            </Box>
                            {activeOperation.steps && activeOperation.steps.length > 0 && (
                                <Typography variant="body2">
                                    Step completati: <strong>{activeOperation.steps.filter(s => s.status === 'ok').length}/{activeOperation.steps.length}</strong>
                                </Typography>
                            )}
                        </Box>

                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Step Eseguiti:
                        </Typography>
                        {activeOperation.steps && activeOperation.steps.length > 0 ? (
                            <List>
                                {activeOperation.steps.map((step, idx) => (
                                    <ListItem key={idx} sx={{ borderLeft: `2px solid ${step.status === 'ok' ? '#4caf50' : '#f44336'}`, pl: 2, mb: 1 }}>
                                        <ListItemText
                                            primary={step.progressLabel}
                                            secondary={
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {new Date(step.timestamp).toLocaleString('it-IT')}
                                                    </Typography>
                                                    {step.status && (
                                                        <Box sx={{ mt: 0.5 }}>
                                                            <Chip
                                                                label={step.status}
                                                                size="small"
                                                                color={step.status === 'ok' ? 'success' : 'error'}
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                Nessuno step disponibile
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {activeOperation && (
                    <Button onClick={() => setActiveOperation(null)}>Indietro</Button>
                )}
                <Button onClick={handleCloseModal}>Chiudi</Button>
            </DialogActions>
        </Dialog>
    );
};

const PubLabInfiniteTable = ({ setSelectedLabProps, setIsShowSelectedLab, setIsShowScheduleLab, setIsShowDropLab,
    setIsShowCloneLab, setIsShowRelaunchLab, setIsShowManageUsersLab, setIsShowManageProxyLab, setIsShowManageAppVolLab, isAdmin, apiAuthGroups }) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const tableContainerRef = useRef(null);
    const rowVirtualizerInstanceRef = useRef(null);

    const [columnFilters, setColumnFilters] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState([{ id: "idLab", desc: true }]);
    const [showOperationsModal, setShowOperationsModal] = useState(false);
    const [selectedLabId, setSelectedLabId] = useState(null);
    const [detailLab, setDetailLab] = useState(null);

    const { flatData, totalDBRowCount, totalFetched, isError, isFetching, isLoading, fetchNextPage, refetch } = useLabData({
        columnFilters, globalFilter, sorting, isAdmin
    });

    // Aggiorna sia detailLab che la cache react-query quando un lab viene modificato
    const handleLabUpdate = useCallback((updatedLab) => {
        setDetailLab(updatedLab);
        // Aggiorna direttamente la cache react-query
        queryClient.setQueryData(
            ['table-data', columnFilters, globalFilter, sorting, isAdmin],
            (oldData) => {
                if (!oldData) return oldData;
                return {
                    ...oldData,
                    pages: oldData.pages.map(page => ({
                        ...page,
                        data: page.data.map(lab =>
                            lab.idLab === updatedLab.idLab
                                ? { ...lab, isPredefinito: updatedLab.isPredefinito }
                                : lab
                        ),
                    })),
                };
            }
        );
    }, [queryClient, columnFilters, globalFilter, sorting, isAdmin]);

    const handleRefreshLab = useCallback(async (idLab) => {
        const result = await refetch();
        const freshLab = result.data?.pages
            ?.flatMap(page => page.data)
            ?.find(lab => lab.idLab === idLab);
        if (freshLab) {
            setDetailLab(freshLab);
        }
    }, [refetch]);

    const handleOpenOperationsModal = useCallback((idLab) => {
        setSelectedLabId(idLab);
        setShowOperationsModal(true);
    }, []);

    const handleCloseOperationsModal = useCallback(() => {
        setShowOperationsModal(false);
        setSelectedLabId(null);
    }, []);

    const columns = useMemo(() => {
        const cols = getColumns();
        return cols.map(col => {
            if (col.accessorKey === 'operations') {
                return {
                    ...col,
                    Cell: ({ cell }) => (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenOperationsModal(cell.row.original.idLab);
                            }}
                            title="Visualizza storico operazioni"
                            sx={{ color: '#a71e3b', '&:hover': { backgroundColor: 'rgba(167,30,59,0.1)' } }}
                        >
                            <SearchIcon fontSize="small" />
                        </IconButton>
                    )
                };
            }
            return col;
        });
    }, [handleOpenOperationsModal]);

    const handleLabAction = useCallback((actionType, labData) => {
        // Usa i dati freschi da flatData se disponibili, altrimenti il fallback su labData
        const freshData = flatData.find(d => d.idLab === labData.idLab) || labData;
        // Functional update: preserva repoPkgSel se è lo stesso lab (potrebbe essere stato
        // aggiornato localmente dopo un save, mentre flatData potrebbe essere ancora stale)
        setSelectedLabProps(prev => {
            if (prev?.idLab === freshData.idLab) {
                return { ...freshData, repoPkgSel: prev.repoPkgSel ?? freshData.repoPkgSel };
            }
            return freshData;
        });
        switch (actionType) {
            case 'modifica': setIsShowSelectedLab(true); break;
            case 'pianifica': setIsShowScheduleLab(true); break;
            case 'cancella': setIsShowDropLab(true); break;
            case 'clona': setIsShowCloneLab(true); break;
            case 'riesegui': setIsShowRelaunchLab(true); break;
            case 'gestisciUtenti': setIsShowManageUsersLab(true); break;
            case 'gestisciProxy': setIsShowManageProxyLab(true); break;
            case 'gestisciAppVol': setIsShowManageAppVolLab(true); break;
            default: break;
        }
    }, [flatData, setSelectedLabProps, setIsShowSelectedLab, setIsShowScheduleLab, setIsShowDropLab, setIsShowCloneLab,
        setIsShowRelaunchLab, setIsShowManageUsersLab, setIsShowManageProxyLab, setIsShowManageAppVolLab]);

    const fetchMoreOnBottomReached = useCallback((containerRefElement) => {
        if (containerRefElement) {
            const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
            if (scrollHeight - scrollTop - clientHeight < 400 && !isFetching && totalFetched < totalDBRowCount) {
                fetchNextPage();
            }
        }
    }, [fetchNextPage, isFetching, totalFetched, totalDBRowCount]);

    useEffect(() => {
        fetchMoreOnBottomReached(tableContainerRef.current);
    }, [fetchMoreOnBottomReached]);

    useEffect(() => {
        try {
            rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
        } catch (error) {
            console.error(error);
        }
    }, [sorting, columnFilters, globalFilter]);

    const table = useMaterialReactTable({
        columns,
        data: flatData,
        // State management
        state: { columnFilters, globalFilter, isLoading, showAlertBanner: isError, showProgressBars: isFetching, sorting },
        // Handlers
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        // Column resizing
        enableColumnResizing: true,
        columnSizingMode: 'onChange',
        // Virtualization
        enableRowVirtualization: true,
        rowVirtualizerInstanceRef,
        rowVirtualizerOptions: { overscan: 4 },
        // Manual modes & features
        manualFiltering: true,
        manualSorting: true,
        enablePagination: false,
        enableFullScreenToggle: true,
        enableExpanding: false,
        enableRowActions: true,
        positionActionsColumn: 'first',
        displayColumnDefOptions: {
            'mrt-row-actions': {
                header: '',
                size: 50,
                muiTableHeadCellProps: { sx: { padding: '0 4px' } },
                muiTableBodyCellProps: { sx: { padding: '0 4px' } },
            },
        },
        renderRowActions: ({ row }) => (
            <IconButton
                size="small"
                onClick={() => setDetailLab(row.original)}
                title="Dettaglio laboratorio"
                sx={{
                    color: '#a71e3b',
                    '&:hover': { backgroundColor: 'rgba(167, 30, 59, 0.1)' },
                }}
            >
                <VisibilityIcon fontSize="small" />
            </IconButton>
        ),

        initialState: {
            showGlobalFilter: true,
            showColumnFilters: false,
            density: 'compact',
        },

        // STYLING PROPS RESTORED FROM ORIGINAL CODE
        muiTableContainerProps: {
            ref: tableContainerRef,
            sx: {
                width: '100%',
                maxWidth: 'none',
                height: 'calc(100vh - 180px)',
                maxHeight: 'calc(100vh - 180px)',
                position: 'relative',
                overflow: 'auto',
                '& .MuiTableContainer-root': {
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    height: '100%',
                },
            },
            onScroll: (event) => fetchMoreOnBottomReached(event.target),
        },
        muiTopToolbarProps: {
            sx: {
                backgroundColor: '#a71e3b',
                color: 'white',
                position: 'sticky',
                top: 0,
                zIndex: 900, // lowered from 1000 to ensure NavBar stays on top
                // Stili specifici per il campo di ricerca per garantire la visibilità
                '& .MuiTextField-root': {
                    '& label': {
                        color: 'rgba(255, 255, 255, 0.8)',
                    },
                    '& label.Mui-focused': {
                        color: 'white',
                    },
                    '& .MuiInputBase-root': {
                        '& input': {
                            color: 'white', // Colore del testo digitato
                        },
                        '& .MuiSvgIcon-root': {
                            color: 'white',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'white',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'white',
                        },
                    },
                },
            }
        },
        muiTableHeadCellProps: {
            sx: {
                fontWeight: 700,
                color: '#222',
                borderBottom: '2px solid #a71e3b',
                fontSize: '0.75rem',
                padding: '6px 6px',
                backgroundColor: '#f6f6f6',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }
        },
        muiTableBodyRowProps: {
            sx: {
                '&:hover': {
                    backgroundColor: 'rgba(167,30,59,0.03)',
                },
            }
        },
        muiTableBodyCellProps: {
            sx: {
                fontSize: '0.80rem',
                padding: '4px 6px',
            }
        },
        renderTopToolbarCustomActions: () => (
            <IconButton
                onClick={() => refetch()}
                disabled={isFetching}
                size="small"
                sx={{
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                }}
            >
                <RefreshIcon sx={{
                    animation: isFetching ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                }} />
            </IconButton>
        ),
        renderBottomToolbarCustomActions: () => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', p: '12px 16px', backgroundColor: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
                <Typography sx={{ color: '#6c757d' }}>
                    Visualizzati <strong>{totalFetched}</strong> di <strong>{totalDBRowCount}</strong> laboratori
                </Typography>
                {isFetching && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#a71e3b' }}>
                        <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Caricamento...</span>
                        </div>
                        <Typography variant="body2" sx={{ color: '#a71e3b', fontWeight: '500' }}>
                            Caricamento...
                        </Typography>
                    </Box>
                )}
            </Box>
        ),
        muiToolbarAlertBannerProps: isError ? { color: 'error', children: 'Errore nel caricamento dei dati' } : undefined,
    });

    return (
        <>
            <Box sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative',
                background: '#f4f4f4',
            }}>
                <Box sx={{
                    p: 3,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                }}>
                    <IconButton
                        onClick={() => navigate(-1)}
                        sx={{
                            color: '#a71e3b',
                            '&:hover': { backgroundColor: 'rgba(167, 30, 59, 0.1)' },
                            flexShrink: 0,
                        }}
                        title="Torna indietro"
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 0.5, color: '#a71e3b' }}>
                            📊 Console di Gestione Laboratori
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            Gestisci, monitora e configura i tuoi laboratori virtuali
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{
                    flex: 1,
                    overflow: 'hidden',
                    position: 'relative',
                    '& .MuiTableContainer-root': {
                        borderRadius: '8px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                        border: '1px solid #e0e0e0',
                    },
                    '@media (max-width: 900px)': {
                        '& .MuiTableContainer-root': {
                            overflowX: 'auto',
                            '& .MuiTable-root': {
                                minWidth: '900px',
                            }
                        }
                    },
                }}>
                    <MaterialReactTable table={table} />
                </Box>
            </Box>

            <OperationHistoryModal
                open={showOperationsModal}
                onClose={handleCloseOperationsModal}
                idLab={selectedLabId}
            />

            {/* Modal dettaglio laboratorio */}
            <Dialog
                open={detailLab !== null}
                onClose={() => setDetailLab(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        overflow: 'hidden',
                    }
                }}
            >
                {detailLab && (
                    <LabDetailPanel lab={detailLab} onAction={handleLabAction} onClose={() => setDetailLab(null)} apiAuthGroups={apiAuthGroups} onLabUpdate={handleLabUpdate} onRefresh={handleRefreshLab} />
                )}
            </Dialog>
        </>
    );
};

export default PubLabInfiniteTable;
